import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  showValidation?: boolean;
  countryCode?: string;
  onCountryCodeChange?: (code: string) => void;
}

const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
];

// Format phone number for display (US format)
const formatPhoneDisplay = (value: string, countryCode: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // US/Canada formatting
  if (countryCode === '+1') {
    if (digits.length === 0) return '';
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
  
  // Default: just return the digits
  return digits;
};

// Convert to E.164 format for storage
const toE164Format = (value: string, countryCode: string): string => {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  
  // If it already starts with +, return as is
  if (value.startsWith('+')) {
    return value.replace(/\D/g, '').slice(0, 1) === '' ? '' : '+' + value.replace(/\D/g, '');
  }
  
  return `${countryCode}${digits}`;
};

// Validate phone number based on country code - country-specific length requirements
const validatePhoneNumber = (phone: string, countryCode: string): boolean => {
  if (!phone) return false;
  
  // Must start with the expected country code
  if (!phone.startsWith(countryCode)) return false;
  
  // Extract just the local number digits (without country code)
  const digits = phone.replace(/\D/g, '');
  const countryDigits = countryCode.replace(/\D/g, '');
  const localDigits = digits.slice(countryDigits.length);
  
  // Country-specific validation based on expected local number lengths
  switch (countryCode) {
    case '+1': // US/Canada - exactly 10 digits for local number
      return localDigits.length === 10;
    case '+44': // UK - 10-11 digits
      return localDigits.length >= 10 && localDigits.length <= 11;
    case '+61': // Australia - 9 digits
      return localDigits.length === 9;
    case '+49': // Germany - 10-11 digits
      return localDigits.length >= 10 && localDigits.length <= 11;
    case '+33': // France - 9 digits
      return localDigits.length === 9;
    case '+81': // Japan - 10 digits
      return localDigits.length === 10;
    case '+86': // China - 11 digits
      return localDigits.length === 11;
    default: // Generic - minimum 7 digits for local number
      return localDigits.length >= 7 && digits.length <= 15;
  }
};

// Extract country code and number from E.164
const parseE164 = (e164: string): { countryCode: string; number: string } => {
  if (!e164 || !e164.startsWith('+')) {
    return { countryCode: '+1', number: '' };
  }
  
  // Try to match known country codes
  for (const { code } of COUNTRY_CODES) {
    if (e164.startsWith(code)) {
      return {
        countryCode: code,
        number: e164.slice(code.length),
      };
    }
  }
  
  // Default to +1 if no match
  return { countryCode: '+1', number: e164.slice(1) };
};

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ 
    className, 
    value, 
    onChange, 
    error,
    showValidation = false,
    countryCode: externalCountryCode,
    onCountryCodeChange,
    disabled,
    ...props 
  }, ref) => {
    const [internalCountryCode, setInternalCountryCode] = React.useState('+1');
    const [displayValue, setDisplayValue] = React.useState('');
    
    const countryCode = externalCountryCode || internalCountryCode;
    
    // Initialize from E.164 value
    React.useEffect(() => {
      if (value) {
        const { countryCode: parsedCode, number } = parseE164(value);
        if (!externalCountryCode) {
          setInternalCountryCode(parsedCode);
        }
        setDisplayValue(formatPhoneDisplay(number, parsedCode));
      } else {
        setDisplayValue('');
      }
    }, [value, externalCountryCode]);
    
    const handleCountryCodeChange = (newCode: string) => {
      if (onCountryCodeChange) {
        onCountryCodeChange(newCode);
      } else {
        setInternalCountryCode(newCode);
      }
      
      // Reformat display with new country code
      const digits = displayValue.replace(/\D/g, '');
      const newDisplay = formatPhoneDisplay(digits, newCode);
      setDisplayValue(newDisplay);
      
      // Update E.164 value
      const e164 = toE164Format(digits, newCode);
      onChange(e164);
    };
    
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const digits = input.replace(/\D/g, '');
      
      // Limit to 10 digits for US/Canada, 15 for others
      const maxDigits = countryCode === '+1' ? 10 : 15;
      const limitedDigits = digits.slice(0, maxDigits);
      
      // Format for display
      const formatted = formatPhoneDisplay(limitedDigits, countryCode);
      setDisplayValue(formatted);
      
      // Convert to E.164 and call onChange
      const e164 = toE164Format(limitedDigits, countryCode);
      onChange(e164);
    };
    
    const isValid = value ? validatePhoneNumber(value, countryCode) : null;
    
    return (
      <div className="relative">
        <div className="flex gap-2">
          <Select
            value={countryCode}
            onValueChange={handleCountryCodeChange}
            disabled={disabled}
          >
            <SelectTrigger className={cn("w-[120px]", disabled && "opacity-50")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_CODES.map(({ code, country, flag }) => (
                <SelectItem key={code} value={code}>
                  <span className="flex items-center gap-2">
                    <span>{flag}</span>
                    <span>{code}</span>
                    <span className="text-xs text-muted-foreground">{country}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="relative flex-1">
            <Input
              type="tel"
              className={cn(
                "pr-10",
                error && "border-red-500",
                isValid && showValidation && "border-green-500",
                className
              )}
              value={displayValue}
              onChange={handleInputChange}
              disabled={disabled}
              ref={ref}
              {...props}
            />
            {showValidation && value && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {isValid ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                )}
              </div>
            )}
          </div>
        </div>
        
        {error && (
          <p className="text-sm text-red-500 mt-1">{error}</p>
        )}
        
        {showValidation && !isValid && value && (
          <p className="text-xs text-muted-foreground mt-1">
            Phone number will be stored in international format (E.164)
          </p>
        )}
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
